import { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { MessageSquare, X, Send, MessageCircle } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useLanguage } from "@/contexts/LanguageContext";

const API = process.env.REACT_APP_BACKEND_URL;
const WA = "https://wa.me/23057299188";

const getSessionId = () => {
  let id = localStorage.getItem("dd_chat_session");
  if (!id) { id = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`; localStorage.setItem("dd_chat_session", id); }
  return id;
};

export default function ChatWidget() {
  const { pathname } = useLocation();
  const { t, lang } = useLanguage();
  const GREETING = t("chat.greeting");
  const SUGGESTIONS = [t("chat.s1"), t("chat.s2"), t("chat.s3"), t("chat.s4")];
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [greeted, setGreeted] = useState(false);
  const endRef = useRef(null);
  const sessionId = useRef(getSessionId());

  useEffect(() => {
    if (!open || messages.length) return;
    fetch(`${API}/api/chatbot/history?session_id=${sessionId.current}`).then(r => r.json()).then(h => {
      if (h.length) { setMessages(h); setGreeted(true); return; }
      typeGreeting();
    }).catch(() => typeGreeting());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, messages.length]);

  const typeGreeting = () => {
    setMessages([{ role: "assistant", text: "" }]);
    let i = 0;
    const tick = () => {
      i += 1;
      setMessages([{ role: "assistant", text: GREETING.slice(0, i) }]);
      if (i < GREETING.length) {
        const ch = GREETING[i - 1];
        const pause = /[.,!?]/.test(ch) ? 220 : ch === " " ? 55 : 28 + Math.random() * 30;
        setTimeout(tick, pause);
      } else setGreeted(true);
    };
    setTimeout(tick, 700);
  };

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, open]);

  const appendLast = (chunk) => setMessages(m => { const c = [...m]; c[c.length - 1] = { ...c[c.length - 1], text: c[c.length - 1].text + chunk }; return c; });

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setInput("");
    setBusy(true);
    setMessages(m => [...m, { role: "user", text: msg }, { role: "assistant", text: "" }]);

    let pending = "";
    let streamDone = false;
    const drain = () => new Promise(resolve => {
      const step = () => {
        if (pending.length) {
          const n = pending.length > 200 ? 2 : 1;
          appendLast(pending.slice(0, n));
          pending = pending.slice(n);
        }
        if (streamDone && !pending.length) return resolve();
        setTimeout(step, 14);
      };
      setTimeout(step, 400);
    });
    const drained = drain();

    try {
      const res = await fetch(`${API}/api/chatbot`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ session_id: sessionId.current, message: msg, lang }) });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n\n");
        buffer = lines.pop();
        for (const line of lines) {
          const payload = line.replace(/^data: /, "");
          if (payload === "[DONE]") continue;
          try { pending += JSON.parse(payload).delta; } catch (err) { console.warn("Bad chat chunk", payload); }
        }
      }
    } catch {
      pending += t("chat.error");
    } finally {
      streamDone = true;
      await drained;
      setBusy(false);
    }
  };

  if (pathname.startsWith("/admin")) return null;

  return (
    <>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0, y: 20, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 20, scale: 0.96 }}
            className="fixed bottom-24 right-4 left-4 sm:left-auto sm:w-[380px] z-50 rounded-3xl border border-border/50 bg-card shadow-2xl flex flex-col overflow-hidden max-h-[70vh]" data-testid="chat-widget">
            <div className="flex items-center justify-between px-4 py-3 bg-coral text-white">
              <div className="flex items-center gap-2"><MessageSquare size={16} /><p className="font-heading font-bold text-sm">{t("chat.title")}</p></div>
              <button onClick={() => setOpen(false)} data-testid="chat-close-btn"><X size={16} /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3 min-h-[240px]" data-testid="chat-messages">
              {messages.map((m, i) => (
                <div key={`${m.role}-${i}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[85%] rounded-2xl px-3.5 py-2 text-sm leading-relaxed whitespace-pre-wrap ${m.role === "user" ? "bg-coral text-white rounded-br-md" : "bg-muted/60 rounded-bl-md"}`} data-testid={`chat-msg-${m.role}`}>
                    {m.text ? renderText(m.text) : <span className="inline-flex gap-1 py-1"><Dot /><Dot d="0.15s" /><Dot d="0.3s" /></span>}
                  </div>
                </div>
              ))}
              {greeted && messages.length <= 1 && (
                <div className="flex flex-wrap gap-1.5 pt-1" data-testid="chat-suggestions">
                  {SUGGESTIONS.map(s => <button key={s} onClick={() => send(s)} className="text-xs px-3 py-1.5 rounded-full border border-border/50 bg-muted/30 hover:bg-coral/10 hover:border-coral/40 transition-colors" data-testid="chat-suggestion">{s}</button>)}
                </div>
              )}
              <div ref={endRef} />
            </div>
            <div className="border-t border-border/40 p-3 space-y-2">
              <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex gap-2">
                <input value={input} onChange={e => setInput(e.target.value)} placeholder={t("chat.placeholder")} className="flex-1 rounded-full bg-muted/40 border border-border/40 px-4 py-2 text-sm outline-none focus:border-coral/50" data-testid="chat-input" />
                <Button type="submit" size="icon" disabled={busy || !input.trim()} className="rounded-full bg-coral hover:bg-coral-600 text-white w-9 h-9 shrink-0" data-testid="chat-send-btn"><Send size={14} /></Button>
              </form>
              <a href={WA} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-1.5 text-[11px] text-muted-foreground hover:text-[#25D366]" data-testid="chat-whatsapp-link"><MessageCircle size={12} /> {t("chat.human")}</a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      <button onClick={() => setOpen(o => !o)} data-testid="chat-toggle-btn"
        className="fixed bottom-5 right-4 z-50 w-14 h-14 rounded-full bg-coral text-white shadow-xl shadow-coral/30 flex items-center justify-center hover:scale-105 transition-transform">
        {open ? <X size={22} /> : <MessageSquare size={22} />}
      </button>
    </>
  );
}

const renderText = (t) => t.split(/(\*\*[^*]+\*\*)/g).map((part, i) => part.startsWith("**") && part.endsWith("**") ? <strong key={i}>{part.slice(2, -2)}</strong> : part);

const Dot = ({ d = "0s" }) => <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/60 animate-bounce" style={{ animationDelay: d }} />;
